// NOTE - extend only in the case when you specifically need to target a specific error
// WHY - code readability
export const ERROR_CODES = {
  OVER_50_BACKLOG_ITEMS: 1071,
  NO_CONTACTS_TO_CALL: 1073,
  PROFILE_BLOCKED: 1008,
  NO_CURRENT_WORKING_CONTACT: 1096,
  USER_NOT_APPROVED_FOR_CAMPAIGN: 1099,
  ONGOING_EMAIL_CHAIN_EXISTS: 1124,
  INVALID_PHONE_NUMBER: 1135,
  NO_EXCLUSIVE_LEADS_AVAILABLE: 1143,
  USER_ALREADY_HAS_LEADS: 1142,
} as const;

// GLE-8249: POST .../contact/:id/move_meeting. Grouped because the move
// modal routes on some of them: EVENT_GONE and NOT_ORGANIZER mean the move can
// never succeed for this event, so the rep is offered the booking flow;
// CONFLICT and RECORD_PENDING mean the event changed under the modal, so the
// card's copy of it is refreshed.
export const MEETING_MOVE_ERROR_CODES = {
  NO_CALENDAR_CONNECTION: 1187,
  NO_WRITE_SCOPE: 1188,
  EVENT_NOT_DETECTED: 1189,
  EVENT_GONE: 1190,
  NOT_ORGANIZER: 1191,
  PROVIDER_FAILED: 1192,
  NO_LIVE_MEETING: 1193,
  TIME_IN_PAST: 1194,
  SAME_TIME: 1195,
  CONFLICT: 1196,
  ANOTHER_REPS_MEETING: 1197,
  // The provider moved the event but the booking update failed; the calendar
  // sync carries the new time over on its own.
  RECORD_PENDING: 1198,
} as const;

// GLE-8421: PATCH .../booked_meeting/:activity_log_id/notes.
export const BOOKED_MEETING_NOTES_ERROR_CODES = {
  NOT_FOUND: 1340,
  ANOTHER_REPS_MEETING: 1341,
  BLANK: 1342,
} as const;

export const GLOBALLY_HANDLED_ERRORS = {
  [ERROR_CODES.USER_NOT_APPROVED_FOR_CAMPAIGN]:
    "You're not approved to work on this campaign.",
} as const;

export const ERRORS = {
  [ERROR_CODES.PROFILE_BLOCKED]: "This account is not allowed on the platforms",
  [1110 as number]: "This campaign isn't available for weekend calling",
  [1026 as number]: "You already have an active session open",
  [1037 as number]: "This lead already exists in our system",
  [1068 as number]:
    "This company is not eligible to be reached out to for this campaign. Sorry!",
  [1069 as number]: "Someone is already working this lead! Sorry.",
  [1070 as number]: "This prospect or account is on the do not call list",
  [ERROR_CODES.OVER_50_BACKLOG_ITEMS]:
    "Backlog limit (50 items) reached. Please remove some items from the backlog to add more.",
  [1072 as number]: "Contact view limit reached",
  [ERROR_CODES.NO_CONTACTS_TO_CALL]: "No contacts to call",
  [1070 as number]:
    "This prospect has specifically requested to be put on the DNC list. Sorry!",
  [1074 as number]: "This campaign does not allow self-sourced leads.",
  [ERROR_CODES.NO_CURRENT_WORKING_CONTACT]: "No current working contact.",
  [1124 as number]:
    "There already exists an ongoing email chain with this prospect.",
  [1127 as number]:
    "The prospect requested not to email. Please respect their request.",
  [ERROR_CODES.ONGOING_EMAIL_CHAIN_EXISTS]:
    "An ongoing email chain exists for this contact.",
  [ERROR_CODES.INVALID_PHONE_NUMBER]: "Please provide a valid phone number.",
  [1137 as number]:
    "You've reached the maximum invalid email overrides for this week. Please try again next week.",
  [ERROR_CODES.NO_EXCLUSIVE_LEADS_AVAILABLE]: "No more leads available.",
  [ERROR_CODES.USER_ALREADY_HAS_LEADS]: "You already have leads assigned.",
  [MEETING_MOVE_ERROR_CODES.NO_CALENDAR_CONNECTION]:
    "The AE's calendar is not connected, so the event cannot be moved from Glencoco",
  [MEETING_MOVE_ERROR_CODES.NO_WRITE_SCOPE]:
    "The AE's calendar connection has no permission to edit events",
  [MEETING_MOVE_ERROR_CODES.EVENT_NOT_DETECTED]:
    "No calendar event was detected for this meeting",
  [MEETING_MOVE_ERROR_CODES.EVENT_GONE]:
    "The event no longer exists on the AE's calendar",
  [MEETING_MOVE_ERROR_CODES.NOT_ORGANIZER]:
    "The AE is not the organizer of this calendar event, so it cannot be moved from Glencoco",
  // A timeout lands here too, and the provider may still have applied the
  // move, so the copy says it was not confirmed rather than refused.
  [MEETING_MOVE_ERROR_CODES.PROVIDER_FAILED]:
    "The AE's calendar did not confirm the change. Please refresh and try again",
  [MEETING_MOVE_ERROR_CODES.NO_LIVE_MEETING]:
    "There is no upcoming meeting to move",
  [MEETING_MOVE_ERROR_CODES.TIME_IN_PAST]: "Pick a time in the future",
  [MEETING_MOVE_ERROR_CODES.SAME_TIME]: "The meeting is already at this time",
  [MEETING_MOVE_ERROR_CODES.CONFLICT]:
    "This meeting changed while it was being moved. Refresh and try again",
  [MEETING_MOVE_ERROR_CODES.ANOTHER_REPS_MEETING]:
    "This meeting was booked by another rep, so it cannot be moved from here",
  [MEETING_MOVE_ERROR_CODES.RECORD_PENDING]:
    "The calendar event was moved, but Glencoco could not update the booking yet. It will update from the calendar shortly",
  [BOOKED_MEETING_NOTES_ERROR_CODES.NOT_FOUND]:
    "This meeting is no longer live, so notes can't be added to it",
  [BOOKED_MEETING_NOTES_ERROR_CODES.ANOTHER_REPS_MEETING]:
    "This meeting was booked by another rep, so notes cannot be added to it from here",
  [BOOKED_MEETING_NOTES_ERROR_CODES.BLANK]: "Notes cannot be blank",
} as const;

export const ERROR_DESCRIPTIONS = {
  [ERROR_CODES.NO_EXCLUSIVE_LEADS_AVAILABLE]:
    "The leads in the pool to pull from have run out. Please try again tomorrow.",
  [ERROR_CODES.USER_ALREADY_HAS_LEADS]:
    "Please call your remaining leads first, then try loading more again.",
} as const;

export const STRIPE_ERRORS = {
  [1064 as number]: "User has no bank account",
};

/** - - - - - - - - - - - SOCKET ERRORS  - - - - - - - - - - - */

export const SOCKET_ERROR_CODES_GROUP_DIALER_LIST = {
  START_SESSION: 1112,
  LEAD_ON_CALL_ALREADY: 1113,
  MEMBERSHIP_ID_NOT_FOUND: 1114,
} as const;

export const SOCKET_ERROR_CODES = {
  GENERAL: 999,

  /**
   * Dialer List
   */
  ...SOCKET_ERROR_CODES_GROUP_DIALER_LIST,
} as const;

export const SOCKET_ERRORS = {
  [SOCKET_ERROR_CODES.GENERAL as number]:
    "Something went wrong. Please reload the page",

  /**
   * Dialer List
   */
  [SOCKET_ERROR_CODES.START_SESSION]:
    "Need to start list dialing session first",
  [SOCKET_ERROR_CODES.LEAD_ON_CALL_ALREADY]: "Lead is already in a call",
  [SOCKET_ERROR_CODES.MEMBERSHIP_ID_NOT_FOUND]:
    "Lead's membership id is not found",
} as const;

/** - - - - - - - - - - - LOGGING ERROR CATEGORIES  - - - - - - - - - - - */

export const ERROR_CATEGORIES = {
  DIALER_LIST: `[DIALER LIST][ERROR]`,
  AUTH: `[AUTHENTICATION][ERROR]`,
  TWILIO: `[TWILIO_CUSTOM][ERROR]`,
  DISPOSITION: `[DISPOSITION][ERROR]`,
  FOLLOW_UP_EMAIL: `[FOLLOW_UP_EMAIL][ERROR]`,
  WS: `[WEBSOCKETS][ERROR]`,
  BOOK_MEETING: "[BOOK_MEETING][ERROR]",
} as const;
